import axios, { type AxiosResponse } from 'axios'
import { api, API_BASE_URL, getCSRFToken } from './api'
import { HTTP_ENDPOINTS } from '../contracts/endpoints'

const logAuthError = (action: string, error: unknown, context: Record<string, unknown> = {}): void => {
  console.error({
    message: 'Frontend auth request failed',
    error: error instanceof Error
      ? { message: error.message, stack: error.stack }
      : { message: String(error) },
    context: {
      action,
      ...context,
    },
  })
}

const ensureTrailingSlash = (value: string): string => (value.endsWith('/') ? value : `${value}/`)

const inferBackendPathPrefix = (baseUrl: string): string => {
  try {
    const parsed = new URL(ensureTrailingSlash(baseUrl))
    const normalized = parsed.pathname.replace(/\/$/, '')
    return normalized === '/' ? '' : normalized
  } catch {
    return ''
  }
}

const inferCallbackPathPrefix = (backendPrefix: string): string => {
  // In Vite dev we proxy backend requests through "/backend", but allauth redirects
  // directly on the backend host where "/backend/*" does not exist.
  if (backendPrefix === '/backend') {
    return ''
  }

  return backendPrefix
}

export interface AuthUser {
  id: number
  email: string
  first_name: string
  last_name: string
}

export interface CurrentUserResponse {
  isAuthenticated: boolean
  user?: AuthUser
  hasCompletedOnboarding?: boolean
}

export interface AccountDeletionResponse {
  status: 'pending_deletion' | 'already_pending'
  request_id: string
  requested_at: string
  scheduled_purge_at: string
  email_delivery: 'sent' | 'failed'
  cancellation_email_sent_at: string | null
  support_email: string
}

/**
 * Fetch the current authenticated user (if any).
 */
export const fetchCurrentUser = async (): Promise<CurrentUserResponse> => {
  try {
    const response: AxiosResponse<CurrentUserResponse> = await api.get('/api/auth/session/')
    return response.data
  } catch (error) {
    logAuthError('fetchCurrentUser', error)
    return { isAuthenticated: false }
  }
}

/**
 * Request logout via the backend API.
 */
export const logoutUser = async (): Promise<void> => {
  try {
    const csrfToken = await getCSRFToken()
    await api.post('/api/auth/logout/', undefined, {
      headers: {
        'X-CSRFToken': csrfToken
      }
    })
  } catch (error) {
    logAuthError('logoutUser', error)
    throw error
  }
}

/**
 * Retrieve the Google OAuth login URL from the backend.
 *
 * @param redirectUrl Optional URL to redirect back to after successful login.
 */
export const getGoogleLoginUrl = async (redirectUrl?: string): Promise<string> => {
  try {
    const params = new URLSearchParams()
    if (redirectUrl) {
      params.set('redirect', redirectUrl)
    } else {
      params.set('redirect', window.location.href)
    }

    const response = await api.get<{ login_url: string }>(`/api/auth/google/login-url/?${params.toString()}`)
    return response.data.login_url
  } catch (error) {
    logAuthError('getGoogleLoginUrl', error, { redirectUrl })

    // Fallback to constructing the URL manually if API call fails
    const backendPrefix = inferBackendPathPrefix(API_BASE_URL)
    const callbackPrefix = inferCallbackPathPrefix(backendPrefix)
    const callbackPath = `${callbackPrefix}/auth/react/callback/`
    const target = redirectUrl ?? window.location.href
    const loginUrl = new URL('accounts/google/login/', ensureTrailingSlash(API_BASE_URL))
    loginUrl.searchParams.set('process', 'login')
    loginUrl.searchParams.set('next', `${callbackPath}?redirect=${encodeURIComponent(target)}`)
    return loginUrl.toString()
  }
}

export const submitAccountDeletion = async (
  confirmationEmail: string
): Promise<AccountDeletionResponse> => {
  try {
    const csrfToken = await getCSRFToken()
    const response = await api.post<AccountDeletionResponse>(
      HTTP_ENDPOINTS.accountDeletion,
      { confirmation_email: confirmationEmail },
      {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken
        }
      }
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError<AccountDeletionResponse>(error)) {
      const data = error.response?.data
      if (error.response?.status === 409 && data?.status === 'already_pending') {
        return data
      }
    }
    logAuthError('submitAccountDeletion', error, { confirmationEmail })
    throw error
  }
}
